import React, { useState, useEffect } from "react";
import "./AddOnSelector.css";
import { supabase } from "../supabase";
import placeholderLogo from "../images/download.svg";

// Check if any selected timeslot falls within 2AM-7AM (F&B unavailable)
const isFnBAvailable = (selectedCourtTimeSlots) => {
  if (!selectedCourtTimeSlots || selectedCourtTimeSlots.size === 0) return true;

  const allTimeslots = Array.from(selectedCourtTimeSlots.values()).flat();
  for (const slot of allTimeslots) {
    // Parse "HH:MM AM|PM" format
    const [time, period] = slot.split(" ");
    let hour = parseInt(time.split(":")[0], 10);
    if (period === "AM" && hour === 12) hour = 0;
    if (period === "PM" && hour !== 12) hour += 12;
    // Unavailable between 2AM (2) and 7AM (6:59)
    if (hour >= 2 && hour < 7) return false;
  }
  return true;
};

const AddOnSelector = ({ selectedSport, selectedCourtTimeSlots, onContinue, onSkip, onSelectionChange, hideActions = false }) => {
  const [addOns, setAddOns] = useState([]);
  const [selectedAddOns, setSelectedAddOns] = useState(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const fnbAvailable = isFnBAvailable(selectedCourtTimeSlots);

  useEffect(() => {
    const fetchAddOns = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const sportId = selectedSport === "Pickle" ? 1 : selectedSport === "Padel" ? 2 : null;

        if (!sportId) {
          throw new Error("Invalid sport selected");
        }

        const { data, error: fetchError } = await supabase.functions.invoke("get-add-on-items", {
          body: { sport_id: sportId },
        });

        if (fetchError) {
          console.error("AddOnSelector.js: Error fetching add-ons:", fetchError);
          throw fetchError;
        }

        if (data && data.data) {
          // Transform response to match component expectations
          const transformedAddOns = data.data.map(cat => ({
            category: cat.category_name,
            category_id: cat.category_id,
            items: cat.items
          }));
          setAddOns(transformedAddOns);
        } else {
          setAddOns([]);
        }
      } catch (err) {
        console.error("AddOnSelector.js: Failed to load add-ons:", err);
        setError("Failed to load add-on items. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAddOns();
  }, [selectedSport]);

  const handleQuantityChange = (addOnId, delta) => {
    setSelectedAddOns((prev) => {
      const newMap = new Map(prev);
      const currentQty = newMap.get(addOnId) || 0;
      const newQty = Math.max(0, currentQty + delta);

      if (newQty === 0) {
        newMap.delete(addOnId);
      } else {
        newMap.set(addOnId, newQty);
      }

      // Notify parent of selection change
      if (onSelectionChange) {
        const addOnsArray = Array.from(newMap.entries()).map(([add_on_id, quantity]) => ({
          add_on_id,
          quantity,
        }));
        const total = calculateTotalFromMap(newMap);
        onSelectionChange(addOnsArray, total);
      }

      return newMap;
    });
  };

  const calculateTotalFromMap = (map) => {
    let total = 0;
    map.forEach((quantity, addOnId) => {
      const addOn = addOns
        .flatMap((category) => category.items)
        .find((item) => item.id === addOnId);
      if (addOn) {
        total += addOn.price * quantity;
      }
    });
    return total;
  };

  const calculateTotal = () => {
    let total = 0;
    selectedAddOns.forEach((quantity, addOnId) => {
      const addOn = addOns
        .flatMap((category) => category.items)
        .find((item) => item.id === addOnId);
      if (addOn) {
        total += addOn.price * quantity;
      }
    });
    return total;
  };

  const handleContinue = () => {
    const addOnsArray = Array.from(selectedAddOns.entries()).map(([add_on_id, quantity]) => ({
      add_on_id,
      quantity,
    }));
    onContinue(addOnsArray);
  };

  const handleSkip = () => {
    onSkip();
  };

  if (isLoading) {
    return (
      <div className="addon-selector-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <span>Loading add-ons...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="addon-selector-container">
        <div className="error-message">{error}</div>
        {!hideActions && (
          <button className="skip-button" onClick={handleSkip}>
            Continue Without Add-Ons
          </button>
        )}
      </div>
    );
  }

  const totalCost = calculateTotal();
  const hasSelections = selectedAddOns.size > 0;

  return (
    <div className="addon-selector-container">
      <h2 className="addon-title">Add-Ons (Optional)</h2>

      {addOns.length === 0 ? (
        <div className="no-addons-message">
          <p>No add-ons available at the moment.</p>
          {!hideActions && (
            <button className="skip-button" onClick={handleSkip}>
              Continue to Checkout
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="addon-categories">
            {addOns.map((category) => (
              <div key={category.category} className="addon-category">
                <h3 className="category-title">{category.category}</h3>
                <div className="addon-items">
                  {category.items.map((item) => {
                    const isFnBCategory = category.category_id === 2;
                    const isUnavailable = /*isFnBCategory &&*/ !fnbAvailable;

                    console.log("isUnavailable:", isUnavailable)
                    return (
                      <div
                        key={item.id}
                        className={`addon-item ${!item.in_stock ? "out-of-stock" : ""} ${isUnavailable ? "unavailable" : ""}`}
                      >
                        <div className={`addon-image ${!item.image_url ? "addon-image-placeholder" : ""}`}>
                          <img src={item.image_url || placeholderLogo} alt={item.name} />
                        </div>
                        <div className="addon-details">
                          <h4 className="addon-name">{item.name}</h4>
                          {item.description && (
                            <p className="addon-description">{item.description}</p>
                          )}
                          <div className="addon-footer">
                            <span className="addon-price">RM {item.price.toFixed(2)}</span>
                            {isUnavailable ? (
                              <span className="unavailable-label">Not available between 2AM - 7AM</span>
                            ) : !item.in_stock ? (
                              <span className="out-of-stock-label">Out of Stock</span>
                            ) : (
                              <div className="quantity-selector">
                                <button
                                  className="quantity-btn"
                                  onClick={() => handleQuantityChange(item.id, -1)}
                                  disabled={!selectedAddOns.has(item.id)}
                                >
                                  -
                                </button>
                                <span className="quantity-display">
                                  {selectedAddOns.get(item.id) || 0}
                                </span>
                                <button
                                  className="quantity-btn"
                                  onClick={() => handleQuantityChange(item.id, 1)}
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {!hideActions && (
            <div className="addon-actions">
              <button className="skip-button" onClick={handleSkip}>
                Skip Add-Ons
              </button>
              <button
                className="continue-button"
                onClick={handleContinue}
                disabled={!hasSelections}
              >
                Continue {hasSelections && `(RM ${totalCost.toFixed(2)})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AddOnSelector;
